import moment from 'moment';
import {analytics} from '../analytics';
import {PaymentSourceTypes} from '../api/generated/enums';
import {
  AccountDetailDto,
  AccountSummaryDto,
  PaymentChannelSummaryDto,
  PaymentSourceConfig,
  PaymentSourceSummaryDto,
} from '../api/generated/index.defs';
import {ApiResponse} from '../types';

export function getAvailablePaymentSourceTypesByChannel(
  channel: PaymentChannelSummaryDto,
  account: AccountDetailDto,
  savedPaymentSources?: PaymentSourceSummaryDto[],
  currentSource?: PaymentSourceSummaryDto
) {
  const {creditCard, ach} = channel;
  const availablePaymentSourceTypes: PaymentSourceTypes[] = [];

  if (ach.enabled && ach.paymentMerchantAccountId && !account.blockAchPayment) {
    availablePaymentSourceTypes.push('ACH');
  }

  if (
    creditCard.enabled &&
    creditCard.paymentMerchantAccountId &&
    !account.blockCreditCardPayment
  ) {
    availablePaymentSourceTypes.push('Credit Card');
  }

  const availablePaymentSources = (savedPaymentSources || []).filter((x) =>
    availablePaymentSourceTypes.includes(x.paymentSourceType as PaymentSourceTypes)
  );

  if (currentSource && !availablePaymentSources.find((x) => x.id === currentSource.id)) {
    availablePaymentSources.push(currentSource);
  }

  return {
    availablePaymentSources,
    paymentSourceTypes: availablePaymentSourceTypes,
  };
}

export function getAvailablePaymentSourceTypes(
  paymentSourceConfig: PaymentSourceConfig,
  account?: AccountSummaryDto | AccountSummaryDto[],
  currentSource?: PaymentSourceSummaryDto
) {
  const {creditCard, ach, paymentSources} = paymentSourceConfig;
  const availablePaymentSourceTypes: PaymentSourceTypes[] = [];

  const accounts = Array.isArray(account) ? account : account ? [account] : []; // FIXME: Think about this

  if (ach.enabled && accounts.every((account) => account?.blockAchPayment !== true)) {
    availablePaymentSourceTypes.push('ACH');
  }

  if (creditCard.enabled && accounts.every((account) => account?.blockCreditCardPayment !== true)) {
    availablePaymentSourceTypes.push('Credit Card');
  }

  let availablePaymentSources = (paymentSources || []).filter((x) =>
    availablePaymentSourceTypes.includes(x.paymentSourceType as PaymentSourceTypes)
  );

  if (currentSource && !availablePaymentSources.find((x) => x.id === currentSource.id)) {
    availablePaymentSources.push(currentSource as PaymentSourceSummaryDto);
  }

  return {
    availablePaymentSources,
    paymentSourceTypes: availablePaymentSourceTypes,
  };
}

export const parseExpirationData = (cardExpiration: string) => {
  const invalidExpiration: ApiResponse<any> = {
    hasErrors: true,
    data: undefined,
    errors: [
      {
        propertyName: 'CardExpiration',
        errorMessage: 'Invalid Card Expiration',
      },
    ],
  };

  const formattedExpiration = cardExpiration.replace(/\s+/g, '');
  if (!(formattedExpiration?.length === 5 || formattedExpiration?.length === 7)) {
    return {
      error: invalidExpiration,
    };
  }

  try {
    const yearPattern = formattedExpiration.length === 5 ? 'MM/YY' : 'MM/YYYY';
    const expiration = moment(formattedExpiration, yearPattern).endOf('month');
    if (!expiration.isValid()) {
      throw new Error('Invalid date');
    }

    return {
      expiration: {
        cardExpirationMonth: expiration.format('MM'),
        cardExpirationYear: expiration.format('YY'),
      },
    };
  } catch (error) {
    analytics.trackEvent('card-connect.invalid-expiration-entered', {
      subject: formattedExpiration,
    });

    return {
      error: {
        hasErrors: true,
        data: undefined,
        errors: [
          {
            propertyName: 'CardExpiration',
            errorMessage: 'Invalid Card Expiration',
          },
        ],
      } as ApiResponse<any>,
    };
  }
};

export const isPaymentSourceExpired = (paymentSource: PaymentSourceSummaryDto): boolean => {
  const expirationDate = moment(
    paymentSource.cardExpirationMonth + '/' + paymentSource.cardExpirationYear,
    'MM/YY'
  ).endOf('M');

  return moment().isAfter(expirationDate);
};
